import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {ColorSet} from '@domain/textile-data/color-set/color-set';
import {HorizontalPositionOfGroupCreelPosition} from '@domain/textile-data/colored-yarn-set/horizontal-position-of-group-creel-position';
import {CreelPosition} from '@domain/textile-data/creel/creel-position';
import {HorizontalPositionOfCreelPosition} from '@domain/textile-data/creel/horizontal-position-of-creel-position';
import {MaterialSet} from '@domain/textile-data/textile-data';
import {YarnSet} from '@domain/textile-data/yarn-set/yarn-set';
import {CreelPositionWithColorsAndYarnTypes} from '@presentation/pages/textile-data/colored-yarn-set/add/creel-position-with-colors-and-yarn-types';
import {UuidUtils} from '@vdw/angular-component-library';
import {cloneDeep, each, findIndex, isEmpty, isEqual, isNil, remove, size} from 'lodash-es';
import {CreelPositionsHelper} from './creel-positions.helper';

@Component({
  selector: 'app-creel-position-details',
  templateUrl: './creel-position-details.component.html',
  styleUrls: ['./creel-position-details.component.scss']
})
export class CreelPositionDetailsComponent implements OnChanges, AfterViewInit {
  @Input() public materialSet: MaterialSet;
  @Input() public creelPosition: CreelPosition;
  @Input() public canShowError = false;
  @Input() public canEditMaterialSet: boolean;
  @Output() public creelPositionHasBeenChanged: EventEmitter<void> = new EventEmitter<void>();
  public creelPositionWithColorsAndYarnTypes: CreelPositionWithColorsAndYarnTypes;
  public isDraggingOver = false;
  public droplistsRecursive: string[] = [];

  private readonly DEFAULT_AMOUNT = 1;
  private readonly creelPositionHelper: CreelPositionsHelper;

  public constructor() {
    this.creelPositionHelper = new CreelPositionsHelper();
  }

  public ngAfterViewInit(): void {
    this.setDroplistsRecursive();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.setCreelPreview();

    const creelPosition = changes.creelPosition;

    if (!isNil(creelPosition) && !creelPosition.firstChange && !isEqual(creelPosition.previousValue, creelPosition.currentValue)) {
      this.deselectAllPreviouslySelectedMaterialTypesAndGroups(creelPosition.previousValue.horizontalPositionsOfCreelPosition);
    }
  }

  public drop(event: CdkDragDrop<HorizontalPositionOfCreelPosition[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.emitCreelPositionChanges();
  }

  public setCreelPreview(): void {
    this.creelPositionWithColorsAndYarnTypes = {
      creelPositionForColors: this.isMaterialSetOfTypeColorSet() ? this.creelPosition : null,
      creelPositionForYarnTypes: this.isMaterialSetOfTypeYarnSet() ? this.creelPosition : null
    };
  }

  public canShowDetailsOfCreelPosition(): boolean {
    return !isNil(this.creelPosition);
  }

  public getCreelPositionViewIndex(): number {
    return findIndex(this.materialSet.creelPositions, this.creelPosition) + 1;
  }

  public canShowWarning(): boolean {
    return this.canShowError && isEmpty(this.creelPosition.horizontalPositionsOfCreelPosition);
  }

  public canShowDuplicate(horizontalPositionsOfCreelPosition: HorizontalPositionOfCreelPosition[]): boolean {
    const selectedHorizontalPositionsOfCreelPosition = horizontalPositionsOfCreelPosition.find((item: HorizontalPositionOfCreelPosition) => item.isSelected);

    if (selectedHorizontalPositionsOfCreelPosition?.isGroup()) {
      return (selectedHorizontalPositionsOfCreelPosition as HorizontalPositionOfGroupCreelPosition).group.length > 1;
    }

    return true;
  }

  public addEmptyGroupToSelectedCreelPosition(): void {
    this.creelPosition.horizontalPositionsOfCreelPosition.unshift(new HorizontalPositionOfGroupCreelPosition(this.DEFAULT_AMOUNT, []));

    this.emitCreelPositionChanges();
    this.setDroplistsRecursive();
  }

  public groupSelectedMaterialSetTypesAndGroups(): void {
    const selectedMaterialSetTypes: HorizontalPositionOfCreelPosition[] = cloneDeep(this.getAllSelectedMaterialSetTypesAndGroups(this.creelPosition.horizontalPositionsOfCreelPosition));

    this.resetAllSelectedMaterialSetTypesAndGroups(selectedMaterialSetTypes);

    this.creelPosition.horizontalPositionsOfCreelPosition.unshift(new HorizontalPositionOfGroupCreelPosition(this.DEFAULT_AMOUNT, selectedMaterialSetTypes));

    this.deleteSelectedMaterialSetTypesAndGroups(this.creelPosition.horizontalPositionsOfCreelPosition);
  }

  public duplicateSelectedMaterialSetTypesAndGroups(): void {
    each(this.getAllSelectedMaterialSetTypesAndGroups(this.creelPosition.horizontalPositionsOfCreelPosition), (item: HorizontalPositionOfCreelPosition) =>
      this.creelPosition.horizontalPositionsOfCreelPosition.unshift(cloneDeep(item))
    );

    this.resetAllSelectedMaterialSetTypesAndGroups(this.creelPosition.horizontalPositionsOfCreelPosition);
  }

  public deleteSelectedMaterialSetTypesAndGroups(horizontalPositionsOfCreelPosition: HorizontalPositionOfCreelPosition[]): void {
    remove(horizontalPositionsOfCreelPosition, (horizontalPositionOfCreelPosition: HorizontalPositionOfCreelPosition) => {
      let result = false;

      if (horizontalPositionOfCreelPosition.isSelected) {
        result = true;
      } else if (horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionOfGroupCreelPosition = horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition;

        if (size(horizontalPositionOfGroupCreelPosition.group) > 0) {
          this.deleteSelectedMaterialSetTypesAndGroups(horizontalPositionOfGroupCreelPosition.group);
        } else {
          result = true;
        }
      }

      return result;
    });

    this.resetAllSelectedMaterialSetTypesAndGroups(horizontalPositionsOfCreelPosition);
  }

  public hasSelectedMaterialSetTypesOrGroups(horizontalPositionsOfCreelPositions: HorizontalPositionOfCreelPosition[]): boolean {
    let result = false;

    for (let i = 0; i < size(horizontalPositionsOfCreelPositions); i++) {
      const horizontalPositionsOfCreelPosition = horizontalPositionsOfCreelPositions[i];

      if (horizontalPositionsOfCreelPosition.isSelected) {
        result = true;
        break;
      } else if (horizontalPositionsOfCreelPosition.isGroup()) {
        const horizontalPositionOfGroupCreelPosition = horizontalPositionsOfCreelPosition as HorizontalPositionOfGroupCreelPosition;

        if (size(horizontalPositionOfGroupCreelPosition.group) > 0) {
          result = this.hasSelectedMaterialSetTypesOrGroups(horizontalPositionOfGroupCreelPosition.group);

          if (result) {
            break;
          }
        }
      }
    }

    return result;
  }

  public canShowCreelPreview(): boolean {
    let result = false;

    if (!isNil(this.creelPosition)) {
      const horizontalPositionOfCreelPosition = this.creelPosition.horizontalPositionsOfCreelPosition[0];

      if (size(this.creelPosition.horizontalPositionsOfCreelPosition) === 1 && horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionOfGroupCreelPosition = horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition;

        if (size(horizontalPositionOfGroupCreelPosition.group) > 0) {
          result = true;
        }
      } else if (!isEmpty(this.creelPosition.horizontalPositionsOfCreelPosition)) {
        result = true;
      }
    }

    return result;
  }

  public emitCreelPositionChanges(): void {
    this.setDroplistsRecursive();
    this.creelPositionHasBeenChanged.emit();
  }

  public deselectAllSelectedMaterialTypesAndGroups(): void {
    this.deselectAllPreviouslySelectedMaterialTypesAndGroups(this.creelPosition.horizontalPositionsOfCreelPosition);
  }

  public canShowMaterialType(horizontalPositionOfCreelPosition: any): boolean {
    return horizontalPositionOfCreelPosition.isColor() || horizontalPositionOfCreelPosition.isYarnType();
  }

  public getAllSelectedMaterialSetTypesAndGroups(horizontalPositionsOfCreelPosition: HorizontalPositionOfCreelPosition[]): HorizontalPositionOfCreelPosition[] {
    const selectedColorsAndGroups: HorizontalPositionOfCreelPosition[] = [];

    each(horizontalPositionsOfCreelPosition, (horizontalPositionOfCreelPosition: HorizontalPositionOfCreelPosition) => {
      if (horizontalPositionOfCreelPosition.isSelected) {
        selectedColorsAndGroups.push(horizontalPositionOfCreelPosition);
      } else if (horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionOfGroupCreelPosition = horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition;

        each(this.getAllSelectedMaterialSetTypesAndGroups(horizontalPositionOfGroupCreelPosition.group), (item: HorizontalPositionOfCreelPosition) => selectedColorsAndGroups.push(item));
      }
    });

    return selectedColorsAndGroups;
  }

  public getPositionOfMaterialTypeCreel(materialTypeCreel: HorizontalPositionOfCreelPosition): number {
    return this.creelPositionHelper.getPositionOfMaterialTypeCreelInList(materialTypeCreel, this.getMaterialTypeCreel().horizontalPositionsOfCreelPosition);
  }

  public isMaterialSetOfTypeColorSet(): boolean {
    return this.materialSet instanceof ColorSet;
  }

  private setDroplistsRecursive(): void {
    setTimeout(() => {
      this.droplistsRecursive = ['droplistCreelPositions'];
      this.creelPosition?.horizontalPositionsOfCreelPosition.forEach((childItem: HorizontalPositionOfGroupCreelPosition) => {
        childItem.id = UuidUtils.generateV4Uuid();
        this.droplistsRecursive = this.droplistsRecursive.concat(this.getIdsRecursive(childItem));
      });

      this.droplistsRecursive.reverse();
    });
  }

  private getIdsRecursive(item: HorizontalPositionOfGroupCreelPosition): string[] {
    let ids = item.group ? [item.id] : [];
    item.group?.forEach((childItem: HorizontalPositionOfGroupCreelPosition) => {
      ids = ids.concat(this.getIdsRecursive(childItem));
    });
    return ids;
  }

  private getMaterialTypeCreel(): CreelPosition {
    return this.isMaterialSetOfTypeColorSet() ? this.creelPositionWithColorsAndYarnTypes.creelPositionForColors : this.creelPositionWithColorsAndYarnTypes.creelPositionForYarnTypes;
  }

  private resetAllSelectedMaterialSetTypesAndGroups(horizontalPositionsOfCreelPosition: HorizontalPositionOfCreelPosition[]): void {
    each(horizontalPositionsOfCreelPosition, (horizontalPositionOfCreelPosition: HorizontalPositionOfCreelPosition) => {
      horizontalPositionOfCreelPosition.isSelected = false;

      if (horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionOfGroupCreelPosition = horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition;

        horizontalPositionOfGroupCreelPosition.isSelected = false;
        this.resetAllSelectedMaterialSetTypesAndGroups(horizontalPositionOfGroupCreelPosition.group);
      }
    });

    this.emitCreelPositionChanges();
  }

  private isMaterialSetOfTypeYarnSet(): boolean {
    return this.materialSet instanceof YarnSet;
  }

  private deselectAllPreviouslySelectedMaterialTypesAndGroups(horizontalPositionOfCreelPositions: HorizontalPositionOfCreelPosition[]): void {
    each(horizontalPositionOfCreelPositions, (horizontalPositionOfCreelPosition: HorizontalPositionOfCreelPosition) => {
      horizontalPositionOfCreelPosition.isSelected = false;

      if (horizontalPositionOfCreelPosition.isGroup()) {
        const horizontalPositionOfGroupCreelPosition: HorizontalPositionOfGroupCreelPosition = horizontalPositionOfCreelPosition as HorizontalPositionOfGroupCreelPosition;
        this.deselectAllPreviouslySelectedMaterialTypesAndGroups(horizontalPositionOfGroupCreelPosition.group);
      }
    });
  }
}
